import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnalysisListItem } from "../types/ai";
import { AnalysisStatusChip, PAGE_SIZES, Pager, fmtDate } from "./aces/Shared";
import { DateField } from "./aces/DateField";
import SideBar from "./SideBar";
import TopBar from "./TopBar";
import Modal from "react-bootstrap/Modal";
import dayjs from "dayjs";
import { Dropdown, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";

interface Props {
  show?: boolean;
  onPressDetailModal?: () => void;
  onPressUserModal?: () => void;
  usershow?: boolean;
  allList: any[];
  questionList: any;
  responsesList: any[];
  loading: boolean;
  mainLoading: boolean;
  role: string | null;
  dropDownList: any[];
  dropdownLoading: boolean;
  canAnalyse: boolean;
  seesAi: boolean;
  aiEnabled: boolean;
  templates: { id: number; title: string }[];
  onAnalyse: (checklistId: number) => void;
  latestByChecklist: Record<string, AnalysisListItem>;
  getResponsesList: (checklistId: number) => Promise<void>;
  getQuestionList: (checklistId: number) => Promise<void>;
  deleteCheckList: (checklistId: number) => Promise<void>;
}

type ColumnKey = "description" | "site" | "dueDate" | "priority" | "attachment" | "submitted" | "reportStatus" | "aiStatus" | "reviewer";

const COLUMNS_KEY = "reportsColumns.v2";
const DEFAULT_COLUMNS: ColumnKey[] = ["description", "site", "priority", "submitted", "reportStatus", "aiStatus"];
const NOT_ANALYSED = "NOT_ANALYSED";
const AI_STATUSES = [NOT_ANALYSED, "QUEUED", "PROCESSING", "DRAFT_READY", "UNDER_REVIEW", "REVIEWED", "FAILED", "SUPERSEDED"];

const readColumns = (): ColumnKey[] => {
  try {
    localStorage.removeItem("reportsVisibleColumns");
    return JSON.parse(localStorage.getItem(COLUMNS_KEY) || "null") || DEFAULT_COLUMNS;
  } catch {
    return DEFAULT_COLUMNS;
  }
};

const SubmittingChecklist = ({
  deleteCheckList,
  mainLoading,
  show,
  onPressDetailModal,
  onPressUserModal,
  usershow,
  allList,
  loading,
  questionList,
  role,
  responsesList,
  getQuestionList,
  getResponsesList,
  dropDownList,
  dropdownLoading,
  canAnalyse,
  seesAi,
  aiEnabled,
  templates,
  onAnalyse,
  latestByChecklist,
}: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [site, setSite] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [priority, setPriority] = useState("");
  const [reviewer, setReviewer] = useState("");
  const [aiStatus, setAiStatus] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(PAGE_SIZES[0]);
  const [columns, setColumns] = useState<ColumnKey[]>(readColumns);

  const columnLabels: Record<ColumnKey, string> = {
    description: t("aces.reports.descriptionReference"),
    site: t("aces.reports.site"),
    dueDate: t("checklist.table.dueDate"),
    priority: t("checklist.table.priority"),
    attachment: t("checklist.table.attachment"),
    submitted: t("aces.reports.submitted"),
    reportStatus: t("aces.reports.reportStatus"),
    aiStatus: t("aces.reports.aiStatus"),
    reviewer: t("aces.reports.reviewer"),
  };
  const allColumns = (Object.keys(columnLabels) as ColumnKey[]).filter((k) => seesAi || (k !== "aiStatus" && k !== "reviewer"));
  const shown = (k: ColumnKey) => columns.includes(k) && allColumns.includes(k);
  const toggleColumn = (k: ColumnKey) => {
    const next = columns.includes(k) ? columns.filter((c) => c !== k) : [...columns, k];
    setColumns(next);
    localStorage.setItem(COLUMNS_KEY, JSON.stringify(next));
  };

  const sites = useMemo(() => Array.from(new Set(allList.map((i) => i.site?.name).filter(Boolean))).sort() as string[], [allList]);
  const reviewers = useMemo(
    () => Array.from(new Set(Object.values(latestByChecklist).map((a) => a.reviewer).filter(Boolean))).sort() as string[],
    [latestByChecklist],
  );

  // The API returns the company's reports in one list, so paging and filtering happen here.
  const filteredList = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allList
      .filter((item) => {
        const latest = latestByChecklist[item.id];
        const submittedDay = item.updatedAt ? dayjs(item.updatedAt).format("YYYY-MM-DD") : "";
        const haystack = [
          item.title, item.description, item.referenceNo, item.shipment?.awb, item.shipment?.flightNo,
          item.shipment?.origin, item.shipment?.destination,
        ];
        return (
          (!q || q === String(item.id) || haystack.some((v) => v?.toLowerCase().includes(q))) &&
          (!from || submittedDay >= from) &&
          (!to || submittedDay <= to) &&
          (!site || item.site?.name === site) &&
          (!templateId || String(item.templateId) === templateId) &&
          (!priority || item.priority?.toLowerCase() === priority.toLowerCase()) &&
          (!reviewer || latest?.reviewer === reviewer) &&
          (!aiStatus || (latest?.status ?? NOT_ANALYSED) === aiStatus)
        );
      })
      .sort((a, b) => dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf());
  }, [allList, latestByChecklist, search, from, to, site, templateId, priority, reviewer, aiStatus]);

  useEffect(() => {
    setPage(0);
  }, [search, from, to, site, templateId, priority, reviewer, aiStatus, size]);

  const clearFilters = () => {
    setSearch(""); setFrom(""); setTo(""); setSite(""); setTemplateId(""); setPriority(""); setReviewer(""); setAiStatus("");
  };

  return (
    <>
      <Modal
        show={usershow}
        onHide={onPressUserModal}
        centered
        className="__modal"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("checklist.responsesTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="__defaultTable">
            {responsesList.length > 0 ? (
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>{t("checklist.userName")}</th>
                    <th>{t("checklist.email")}</th>
                    <th>{t("checklist.department")}</th>
                    <th>{t("checklist.role")}</th>
                    <th>{t("checklist.status")}</th>
                    <th>{t("checklist.response")}</th>
                  </tr>
                </thead>
                <tbody>
                  {responsesList.map((res, idx) => (
                    <tr key={idx}>
                      <td>{res.user?.name || "N/A"}</td>
                      <td className="email_cell">{res.user?.email || "N/A"}</td>
                      <td>{res.user?.department || "N/A"}</td>
                      <td>{res.user?.role || "N/A"}</td>
                      <td>{res.user?.status || "N/A"}</td>
                      <td>
                        {res.response ? (
                          <a
                            href={res.response}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t("checklist.submitted")}
                          </a>
                        ) : (
                          t("checklist.not_submitted")
                        )}
                      </td>
                      <td>
                        {res.submissionDate
                          ? fmtDate(res.submissionDate)
                          : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="nofound">{t("checklist.noResponses")}</p>
            )}
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={show}
        onHide={onPressDetailModal}
        centered
        className="__modal"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("checklist.questionsTitle")}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Manager Section */}
          <h5 className="mb-3">{t("checklist.managerQuestions")}</h5>
          <div className="__defaultTable">
            {questionList?.manager?.length > 0 ? (
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>{t("checklist.id")}</th>
                    <th>{t("checklist.title")}</th>
                    <th>{t("checklist.answer")}</th>
                    <th>{t("checklist.note")}</th>
                    <th>{t("checklist.updatedAt")}</th>
                  </tr>
                </thead>
                <tbody>
                  {questionList.manager.map((q: any, idx: number) => (
                    <tr key={`manager-${idx}`}>
                      <td>{q.id}</td>
                      <td>{q.title}</td>
                      <td>{q.answer || "-"}</td>
                      <td>{q.note || "-"}</td>
                      <td>
                        {fmtDate(q.updatedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>{t("checklist.noManagerQuestions")}</p>
            )}
          </div>
          {/* Employee Section */}
          <h5 className="mt-4 mb-3">{t("checklist.employeeQuestions")}</h5>
          <div className="__defaultTable">
            {questionList?.employee?.length > 0 ? (
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>{t("checklist.id")}</th>
                    <th>{t("checklist.title")}</th>
                    <th>{t("checklist.isImageRequired")}</th>
                    <th>{t("checklist.note")}</th>
                    <th>{t("checklist.updatedAt")}</th>
                  </tr>
                </thead>
                <tbody>
                  {questionList.employee.map((q: any, idx: number) => (
                    <tr key={`employee-${idx}`}>
                      <td>{q.id}</td>
                      <td>{q.title}</td>
                      <td>{q.isImage ? t("aces.common.yes") : t("aces.common.no")}</td>
                      <td>{q.note || "-"}</td>
                      <td>
                        {fmtDate(q.updatedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>{t("checklist.noEmployeeQuestions")}</p>
            )}
          </div>
        </Modal.Body>
      </Modal>

      <div className="__dashboard">
        <div className="__dashboard__wrapper">
          <SideBar />
          <div className="__dashboardRightSide">
            <TopBar />
            <div className="__dashboardContent">
              <div className="__pageHeading">
                <div className="__pageHeading">
                  {/* {t("common.submitted_checklist")} */}
                  {t("common.reports")}
                </div>
              </div>
              <div className="__myTraining">
                <div className="aces-filters">
                  <div className="aces-field" style={{ flexBasis: 280 }}>
                    <label htmlFor="reports-search">{t("aces.reports.searchDescription")}</label>
                    <input id="reports-search" className="form-control" placeholder={t("aces.reports.searchDescription")} value={search} onChange={(e) => setSearch(e.target.value)} />
                  </div>
                  <div className="aces-field">
                    <label htmlFor="reports-from">{t("aces.reports.from")}</label>
                    <DateField id="reports-from" value={from} onChange={setFrom} />
                  </div>
                  <div className="aces-field">
                    <label htmlFor="reports-to">{t("aces.reports.to")}</label>
                    <DateField id="reports-to" value={to} onChange={setTo} />
                  </div>
                  <div className="aces-field">
                    <label htmlFor="reports-site">{t("aces.reports.site")}</label>
                    <select id="reports-site" className="form-control" value={site} onChange={(e) => setSite(e.target.value)}>
                      <option value="">{t("aces.reports.all")}</option>
                      {sites.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="aces-field">
                    <label htmlFor="reports-template">{t("aces.reports.template")}</label>
                    <select id="reports-template" className="form-control" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                      <option value="">{t("aces.reports.all")}</option>
                      {templates.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
                    </select>
                  </div>
                  <div className="aces-field">
                    <label htmlFor="reports-priority">{t("checklist.table.priority")}</label>
                    <select id="reports-priority" className="form-control" value={priority} onChange={(e) => setPriority(e.target.value)}>
                      <option value="">{t("aces.reports.all")}</option>
                      <option value="High">{t("checklistForm.high")}</option>
                      <option value="Medium">{t("checklistForm.medium")}</option>
                      <option value="Low">{t("checklistForm.low")}</option>
                    </select>
                  </div>
                  {seesAi && (
                    <>
                      <div className="aces-field">
                        <label htmlFor="reports-reviewer">{t("aces.reports.reviewer")}</label>
                        <select id="reports-reviewer" className="form-control" value={reviewer} onChange={(e) => setReviewer(e.target.value)}>
                          <option value="">{t("aces.reports.all")}</option>
                          {reviewers.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                      <div className="aces-field">
                        <label htmlFor="reports-ai">{t("aces.reports.aiStatus")}</label>
                        <select id="reports-ai" className="form-control" value={aiStatus} onChange={(e) => setAiStatus(e.target.value)}>
                          <option value="">{t("aces.reports.all")}</option>
                          {AI_STATUSES.map((s) => (
                            <option key={s} value={s}>{s === NOT_ANALYSED ? t("aces.reports.notAnalysed") : t(`aces.enum.analysis.${s}`)}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                  <div className="aces-actions">
                    <button type="button" className="aces-btn secondary" onClick={clearFilters}>{t("aces.reports.clear")}</button>
                    <Dropdown autoClose="outside">
                      <Dropdown.Toggle as="button" type="button" className="aces-btn secondary">
                        <i className="ri-layout-column-line" aria-hidden="true"></i> {t("aces.reports.columns")}
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="aces-column-menu">
                        {allColumns.map((k) => (
                          <label key={k} className="aces-check">
                            <input type="checkbox" checked={columns.includes(k)} onChange={() => toggleColumn(k)} /> {columnLabels[k]}
                          </label>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
                <div className="__defaultTable aces-table-scroll aces-reports-table">
                  <table>
                    <thead>
                      <tr>
                        <th>{t("checklist.table.title")}</th>
                        {shown("description") && <th>{columnLabels.description}</th>}
                        {shown("site") && <th>{columnLabels.site}</th>}
                        {shown("dueDate") && <th>{columnLabels.dueDate}</th>}
                        {shown("priority") && <th>{columnLabels.priority}</th>}
                        {shown("attachment") && <th>{columnLabels.attachment}</th>}
                        {shown("submitted") && <th>{columnLabels.submitted}</th>}
                        {shown("reportStatus") && <th>{columnLabels.reportStatus}</th>}
                        {shown("aiStatus") && <th>{columnLabels.aiStatus}</th>}
                        {shown("reviewer") && <th>{columnLabels.reviewer}</th>}
                        <th>{t("checklist.table.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mainLoading ? (
                        <tr>
                          <td colSpan={allColumns.length + 2} style={{ textAlign: "center" }}>
                            <i className="ri-loader-4-line ri-spin" style={{ fontSize: "20px" }} aria-hidden="true"></i> {t("aces.reports.loading")}
                          </td>
                        </tr>
                      ) : filteredList.length > 0 ? (
                        filteredList.slice(page * size, (page + 1) * size).map((item) => {
                          const latest = latestByChecklist[item.id];
                          return (
                            <tr key={item.id}>
                              <td>
                                {item.title}
                                {!shown("description") && item.referenceNo && <div className="aces-reference">{item.referenceNo}</div>}
                                {!shown("description") && item.shipment?.awb && <div className="aces-reference">AWB {item.shipment.awb}</div>}
                                {canAnalyse && aiEnabled && !latest && (
                                  <div><span className="aces-chip blue"><i className="ri-sparkling-2-line" aria-hidden="true"></i> {t("aces.reports.aiAvailable")}</span></div>
                                )}
                              </td>
                              {shown("description") && (
                                <td className="aces-description">
                                  {item.description && <div className="aces-clamp" title={item.description}>{item.description}</div>}
                                  {item.referenceNo && <div className="aces-reference">{item.referenceNo}</div>}
                                  {item.shipment?.awb && <div className="aces-reference">AWB {item.shipment.awb}</div>}
                                </td>
                              )}
                              {shown("site") && <td>{item.site?.name || "—"}</td>}
                              {shown("dueDate") && <td>{item.dueDateTime ? fmtDate(item.dueDateTime) : "-"}</td>}
                              {shown("priority") && (
                                <td>
                                  {item.priority && (
                                    <span className={`priorityBtn ${item.priority.toLowerCase()}Priority`}>
                                      {t(`checklistForm.${item.priority.toLowerCase()}`, { defaultValue: item.priority })}
                                    </span>
                                  )}
                                </td>
                              )}
                              {shown("attachment") && (
                                <td>
                                  {item.attachments?.length > 0 ? (
                                    item.attachments.map((file: any, index: number) => (
                                      <a key={file.id || index} href={file.url} target="_blank" rel="noopener noreferrer"
                                        aria-label={t("aces.a11y.attachment", { n: index + 1 })} title={t("aces.a11y.attachment", { n: index + 1 })} className="attachmentIcon">
                                        <i className="ri-attachment-2" aria-hidden="true"></i>
                                      </a>
                                    ))
                                  ) : (
                                    <span className="aces-no-file">{t("aces.reports.noFile")}</span>
                                  )}
                                </td>
                              )}
                              {shown("submitted") && <td>{fmtDate(item.updatedAt)}</td>}
                              {shown("reportStatus") && <td><span className="aces-chip grey">{t("aces.reports.submitted")}</span></td>}
                              {shown("aiStatus") && (
                                <td>
                                  {latest ? <AnalysisStatusChip value={latest.status} /> : <span className="aces-chip grey">{t("aces.reports.notAnalysed")}</span>}
                                  {latest?.hasUrgent && <div className="mt-1"><span className="aces-chip darkred">{t("aces.register.critical")}</span></div>}
                                  {latest?.reviewer && !shown("reviewer") && <div className="aces-reference mt-1">{latest.reviewer}</div>}
                                </td>
                              )}
                              {shown("reviewer") && <td>{latest?.reviewer || "—"}</td>}
                              <td>
                                {!loading ? (
                                  <div className="aces-row-actions">
                                    <div className="aces-row-links">
                                      <button type="button" className="aces-table-link" onClick={() => getResponsesList(item.id)}>{t("aces.reports.viewReport")}</button>
                                      <button type="button" className="aces-table-link" onClick={() => getQuestionList(item.id)}>{t("aces.reports.questions")}</button>
                                    </div>
                                    {latest ? (
                                      <button type="button" className="aces-btn small" onClick={() => navigate(`/ai/analysis/${latest.id}`)}>{t("aces.reports.viewAnalysis")}</button>
                                    ) : canAnalyse && (
                                      <button type="button" className="aces-btn small" onClick={() => onAnalyse(item.id)}>
                                        <i className="ri-sparkling-2-line" aria-hidden="true"></i> {t("aces.analyseButton")}
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <Spinner animation="border" role="status" size="sm">
                                    <span className="visually-hidden">Loading...</span>
                                  </Spinner>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td className="nofound text-center" colSpan={allColumns.length + 2}>
                            {t("checklist.noChecklistFound")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <Pager page={page} size={size} total={filteredList.length} onPage={setPage} onSize={setSize} />
              </div>
            </div>
          </div>
        </div>
      </div>

    </>
  );
};

export default SubmittingChecklist;
